import { isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { environment } from 'src/environments/environment';

// Type declaration for google object
declare global {
  interface Window {
    google: any;
  }
}

declare const google: any;

@Injectable({
  providedIn: 'root'
})
export class InitialService {
  platFormId = inject(PLATFORM_ID);
  
  async setUpGoogleScript(): Promise<void> {
    if (isPlatformBrowser(this.platFormId)) {
      return new Promise<void>((resolve) => {
        // Check if Google Maps API is already loaded
        if (typeof google !== 'undefined' && google.maps && google.maps.places) {
          resolve();
          return;
        }

        // Check if script is already being loaded
        const existingScript = document.getElementById('googleMapsScript');
        if (existingScript) {
          // Script is already being loaded, wait for it
          existingScript.addEventListener('load', () => resolve());
          return;
        }

        // Create and load the script
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.src = `https://maps.googleapis.com/maps/api/js?key=${environment.googleKey}&libraries=places&language=en`;
        script.id = 'googleMapsScript';
        script.async = true;
        
        // Wait for the script to load
        script.onload = () => {
          // Add a small delay to ensure the API is fully initialized
          setTimeout(() => {
            resolve();
          }, 100);
        };
        
        script.onerror = () => {
          console.error('Failed to load Google Maps API');
          resolve(); // Resolve anyway to prevent hanging
        };
        
        document.head.appendChild(script);
      });
    } else {
      // For server-side rendering, just resolve immediately
      return Promise.resolve();
    }
  }

  /**
   * Check if Google Maps API is ready to use
   */
  isGoogleMapsReady(): boolean {
    if (isPlatformBrowser(this.platFormId)) {
      return typeof google !== 'undefined' && 
             google.maps && 
             google.maps.places && 
             google.maps.places.PlacesService;
    }
    return false;
  }

  /**
   * Wait for Google Maps API to be ready
   */
  waitForGoogleMaps(): Promise<void> {
    return new Promise<void>((resolve) => {
      if (this.isGoogleMapsReady()) {
        resolve();
        return;
      }

      // Poll for Google Maps API availability
      const checkInterval = setInterval(() => {
        if (this.isGoogleMapsReady()) {
          clearInterval(checkInterval);
          // Add a small delay to ensure everything is fully initialized
          setTimeout(() => {
            resolve();
          }, 50);
        }
      }, 100);

      // Timeout after 15 seconds
      setTimeout(() => {
        clearInterval(checkInterval);
        console.error('Timeout waiting for Google Maps API to load');
        resolve();
      }, 15000);
    });
  }
}
